import { useQuery } from "@tanstack/react-query";
import { api, euro } from "@/lib/api";

const KPI = ({ label, now, before, fmt = (v) => v, testid }) => {
  const d = before ? Math.round(((now - before) / before) * 100) : null;
  return (
    <div data-testid={testid} className="rounded-sm border border-[var(--line)] p-4">
      <p className="text-xs uppercase tracking-[0.16em] text-[var(--parchment)]/75">{label}</p>
      <p className="mt-1 font-display text-3xl">{fmt(now ?? 0)}</p>
      <p className="text-xs text-[var(--parchment)]/80">{d === null ? "pas de période précédente" : `${d >= 0 ? "+" : ""}${d} % vs 30 jours avant`}</p>
    </div>
  );
};

const Th = ({ children }) => <th className="py-2 pr-4 text-left text-xs font-normal uppercase tracking-[0.14em] text-[var(--parchment)]/75">{children}</th>;

function Channels({ rows }) {
  return (
    <div className="overflow-x-auto">
      <table data-testid="cockpit-channels" className="w-full min-w-[560px] text-sm">
        <thead><tr><Th>Canal</Th><Th>Visiteurs</Th><Th>Commandes</Th><Th>CA</Th><Th>Conversion</Th></tr></thead>
        <tbody>{rows.map((r) => (
          <tr key={r.channel} className="border-t border-[var(--line)]">
            <td className="py-2 pr-4 font-semibold">{r.channel}</td><td className="pr-4">{r.visitors}</td><td className="pr-4">{r.orders}</td>
            <td className="pr-4">{euro(r.revenue)}</td>
            <td className={`pr-4 ${r.visitors > 100 && !r.orders ? "font-semibold text-red-300" : ""}`}>{r.conversion === null ? "—" : `${r.conversion} %`}</td>
          </tr>))}
        </tbody>
      </table>
    </div>
  );
}

function Targets({ rows, now }) {
  return (
    <div className="overflow-x-auto">
      <table data-testid="cockpit-targets" className="w-full min-w-[520px] text-sm">
        <thead><tr><Th>Objectif</Th><Th>Visiteurs / 30 j</Th><Th>Commandes / 30 j</Th><Th>Conversion</Th><Th>Clics Google / 28 j</Th></tr></thead>
        <tbody>
          <tr className="border-t border-[var(--line)] text-[var(--parchment)]/80"><td className="py-2 pr-4">Aujourd'hui</td><td>{now.visitors}</td><td>{now.orders}</td><td>{now.conversion} %</td><td>—</td></tr>
          {rows.map((t) => (
            <tr key={t.horizon} className="border-t border-[var(--line)]"><td className="py-2 pr-4 font-semibold text-[var(--gold-soft)]">{t.horizon}</td>
              <td>{t.visitors}</td><td>{t.orders}</td><td>{t.conversion} %</td><td>{t.gsc_clicks ?? "à connecter"}</td></tr>))}
        </tbody>
      </table>
    </div>
  );
}

/** Tableau de pilotage : situation de référence, objectifs, canaux, marchés, connexions. */
function Plan({ plan }) {
  const weeks = Array.isArray(plan?.weeks) ? plan.weeks : [];
  return (
    <div data-testid="cockpit-plan">
      <h4 className="mb-3 font-display text-xl">Plan des 30 prochains jours</h4>
      <div className="grid gap-4 md:grid-cols-2">
        {weeks.map((w, i) => (
          <div key={w.dates} data-testid={`cockpit-plan-week-${i}`} className="rounded-sm border border-[var(--line)] p-4 text-sm">
            <p className="text-xs uppercase tracking-[0.16em] text-[var(--gold-soft)]">Semaine {i + 1} · {w.dates}</p>
            <p className="mt-1 font-display text-lg">{w.theme}</p>
            <p className="text-xs text-[var(--parchment)]/80">Objectif : {w.goal}</p>
            <ul className="mt-3 space-y-1.5 text-[var(--parchment)]/90">
              <li><b>Articles :</b> {(w.seo || []).join(" · ")}</li><li><b>Réseaux :</b> {w.social}</li>
              <li><b>Communauté :</b> {w.community}</li><li><b>Partenaires :</b> {w.partners}</li><li><b>Indicateur :</b> {w.kpi}</li>
            </ul>
          </div>))}
      </div>
    </div>
  );
}

/** Tableau de pilotage : situation de référence, objectifs, canaux, marchés, connexions. */
export function GrowthCockpit() {
  const { data } = useQuery({ queryKey: ["growth-cockpit"], queryFn: () => api.get("/admin/growth/cockpit").then((r) => r.data), refetchInterval: 300000 });
  if (!data) return <div data-testid="cockpit-loading" className="h-40" />;
  const c = data.current || {}, p = data.previous || {};
  return (
    <section data-testid="growth-cockpit" className="mb-12 space-y-8">
      <div>
        <h3 className="font-display text-3xl">Pilotage de la croissance</h3>
        <p className="mt-1 text-sm text-[var(--parchment)]/80">30 derniers jours complets ({data.period?.[0]} → {data.period?.[1]}), comparés aux 30 jours d'avant.</p>
      </div>
      <div className="grid grid-cols-2 gap-4 md:grid-cols-5">
        <KPI testid="cockpit-visitors" label="Visiteurs" now={c.visitors} before={p.visitors} />
        <KPI testid="cockpit-orders" label="Commandes" now={c.orders} before={p.orders} />
        <KPI testid="cockpit-revenue" label="Chiffre d'affaires" now={c.revenue} before={p.revenue} fmt={euro} />
        <KPI testid="cockpit-conversion" label="Conversion" now={c.conversion} before={p.conversion} fmt={(v) => `${v} %`} />
        <KPI testid="cockpit-search" label="Clics Google (28 j)" now={data.search?.clicks ?? 0} before={null} />
      </div>
      <div><h4 className="mb-3 font-display text-xl">Ventes par canal</h4><Channels rows={Array.isArray(data.channels) ? data.channels : []} />
        <p className="mt-2 text-xs text-[var(--parchment)]/75">En rouge : plus de 100 visiteurs sans aucune commande, canal à corriger en priorité.</p></div>
      <div><h4 className="mb-3 font-display text-xl">Objectifs proposés</h4><Targets rows={Array.isArray(data.targets) ? data.targets : []} now={c} /></div>
      <Plan plan={data.plan} />
      <div className="grid gap-8 md:grid-cols-2">
        <div><h4 className="mb-3 font-display text-xl">Marchés (visiteurs)</h4>
          <ul data-testid="cockpit-markets" className="space-y-1 text-sm">{(data.markets || []).map((m) => <li key={m.country} className="flex justify-between border-b border-[var(--line)] py-1"><span>{m.country}</span><b>{m.visitors}</b></li>)}</ul></div>
        <div><h4 className="mb-3 font-display text-xl">Connexions et automatisations</h4>
          <ul data-testid="cockpit-connections" className="space-y-2 text-sm">{(data.connections || []).map((x) => (
            <li key={x.name} className="border-b border-[var(--line)] pb-2"><span className="font-semibold">{x.name}</span>
              <span className={`ml-2 rounded-full px-2 py-0.5 text-xs ${x.status === "connecté" || x.status === "ok" || x.status === "prêt" ? "bg-emerald-900/60 text-emerald-200" : "bg-amber-900/60 text-amber-200"}`}>{x.status}</span>
              <br /><span className="text-xs text-[var(--parchment)]/80">{x.detail}</span></li>))}</ul></div>
      </div>
    </section>
  );
}
